import { createContext, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ToastContainer, toast } from 'react-toastify';
import {
  Button,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Form,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import { Rating, Star } from '@smastrom/react-rating';
import dayjs from 'dayjs';
import useIsMobileViewport from '../hooks/useIsMobileViewpoint';
import { getSurveyInfo, submitSurvey } from '../services/pcbff';

import '@smastrom/react-rating/style.css';

export const SurveyContext = createContext();

export const useSurvey = () => {
  const context = useContext(SurveyContext);
  if (!context) {
    throw new Error('useSurvey must be used within a SurveyProvider');
  }

  return context;
};

export const SurveyProvider = ({ children }) => {
  const [isDisplay, setIsDisplay] = useState(false);
  const [surveyId, setSurveyId] = useState('');
  const [title, setTitle] = useState('');
  const [user, setUser] = useState({});
  const [customerId, setCustomerId] = useState('');
  const [transactionId, setTransactionId] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [isSurveySubmitted, setSurveySubmitted] = useState(false);
  const isMobile = useIsMobileViewport();
  const [answers, setAnswers] = useState({
    question1: {
      rate: null,
      reason: '',
    },
    question2: {
      rate: [null, null, null, null, null],
    },
    question3: {
      answer: '',
    },
  });
  const { t } = useTranslation();

  const handleClickNPSRate = rate => {
    answers.question1.rate = rate;
    setAnswers({ ...answers });
  };

  const handleChangeQuestion1Reason = e => {
    answers.question1.reason = e.target.value;
    setAnswers({ ...answers });
  };

  const handleChangeQuestion2Rate = (order, rate) => {
    answers.question2.rate[order] = rate;
    setAnswers({ ...answers });
  };

  const handleChangeQuestion3Answer = e => {
    answers.question3.answer = e.target.value;
    setAnswers({ ...answers });
  };

  const checkHaveToShowSurvey = async (user, params) => {
    const response = await getSurveyInfo(user);
    if (response?.status?.code === 0 && response?.body?.popup) {
      setSurveyId(response?.body?.surveyId);
      setIsDisplay(true);
      setTitle(params.title);
      setUser(user);
      setTransactionId(params.transactionId || null);
      setStartDate(dayjs().format('YYYY-MM-DD HH:mm:ss.SSS'));
      return true;
    }
    return false;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsDisplay(false);

    const body = {
      title,
      clientId: user.username,
      surveyId,
      transactionId,
      action: 'SUBMIT',
      surveyStartTime: startDate,
      surveyEndTime: dayjs().format('YYYY-MM-DD HH:mm:ss.SSS'),
      answers: {
        npsScore: answers.question1.rate,
        likelyToRecommendReasons: answers.question1.reason,
        overallExperience: answers.question2.rate[0],
        clearInformationInstructions: answers.question2.rate[1],
        userFriendlyNavigation: answers.question2.rate[2],
        easyToGetThingsDone: answers.question2.rate[3],
        relevantFeaturesForMe: answers.question2.rate[4],
        improvement: answers.question3.answer,
      },
    };
    try {
      const result = await submitSurvey(user, body);
      if (result.status.code === 0) {
        toast.success(t('survey.thank_you'), {
          position: 'bottom-right',
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: false,
          theme: 'light',
        });
        setIsDisplay(false);
        setTimeout(() => {
          setSurveySubmitted(true);
        }, 2000);
      } else {
        toast.error(t('survey.failure'), {
          position: 'bottom-right',
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: false,
          theme: 'light',
        });
      }
    } catch (error) {
      toast.error(t('survey.failure'), {
        position: 'bottom-right',
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: false,
        theme: 'light',
      });
    }
  };

  const handleClose = async () => {
    const body = {
      title,
      clientId: customerId,
      surveyId,
      transactionId,
      action: 'CLOSE',
      surveyStartTime: startDate,
      surveyEndTime: dayjs().add(7, 'hour').format('YYYY-MM-DD HH:mm:ss.SSS'),
      answers: {
        npsScore: '',
        overallExperience: '',
        clearInformationInstructions: '',
        userFriendlyNavigation: '',
        easyToGetThingsDone: '',
        relevantFeaturesForMe: '',
        improvement: '',
        likelyToRecommendReasons: '',
      },
    };
    await submitSurvey(user, body);
    setSurveySubmitted(true);
    setIsDisplay(false);
  };

  const disabledCondiiton =
    answers.question1.rate === null ||
    answers.question2.rate.some(rate => rate === null || rate < 1);

  return (
    <SurveyContext.Provider
      value={{
        isDisplay,
        setIsDisplay,
        checkHaveToShowSurvey,
        isSurveySubmitted,
      }}
    >
      {children}
      {/* <Button color="primary" onClick={() => setIsDisplay(!isDisplay)}>
        Open
      </Button> */}
      <Offcanvas
        direction={isMobile ? 'bottom' : 'end'}
        isOpen={isDisplay}
        className="survey-popup"
      >
        <OffcanvasHeader tag="div" className="">
          <button
            type="button"
            className="btn-close"
            onClick={handleClose}
          ></button>
          <h5>{t('survey.title')}</h5>
        </OffcanvasHeader>
        <OffcanvasBody>
          <Form>
            <div className="title">{t('survey.sub_title')}</div>
            <div className="question">{t('survey.question1.main')}</div>
            <div className="d-flex justify-content-between suggest-rate">
              <strong>{t('survey.question1.min')}</strong>
              <strong>{t('survey.question1.max')}</strong>
            </div>
            <div className="d-flex justify-content-between mb-4">
              {Array.from({ length: 11 }).map((_, index) => (
                <div
                  key={index}
                  className={`rate-number-button ${
                    answers.question1.rate === index ? 'active' : ''
                  }`}
                  onClick={() => handleClickNPSRate(index)}
                >
                  {index}
                </div>
              ))}
            </div>
            <FormGroup className="qustion1-reason">
              <Label for="question1Reason" className="mb-3">
                {t('survey.question1.reason')}
              </Label>
              <Input
                id="question1Reason"
                className="mb-2"
                name="text"
                type="textarea"
                rows="3"
                placeholder={t('survey.question1.reason_placeholder')}
                maxLength={500}
                onChange={handleChangeQuestion1Reason}
              />
              <span className="limit-length-text">
                {answers.question1.reason.length}/500
              </span>
            </FormGroup>
            <hr />
            <br />
            <div className="question">{t('survey.question2.main')}</div>
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="row mb-4">
                <div className="col-7">
                  <b>{t(`survey.question2.sub${index + 1}`)}</b>
                </div>
                <div className="col-5 text-right">
                  <Rating
                    // className="rate-stars"
                    onChange={rate => handleChangeQuestion2Rate(index, rate)}
                    spaceBetween={'small'}
                    // size={18}
                    itemStyles={{
                      itemShapes: Star,
                      itemStrokeWidth: 2,
                      activeFillColor: '#FFB800',
                      activeStrokeColor: '#FFB800',
                      inactiveFillColor: '#F7F7F7',
                      inactiveStrokeColor: '#BEC5D5',
                    }}
                    value={answers.question2.rate[index]}
                  />
                </div>
              </div>
            ))}
            <hr />
            <br />
            <div className="question">{t('survey.question3.main')}</div>
            <Input
              id="question1Reason"
              className="mb-2"
              name="text"
              type="textarea"
              rows="3"
              placeholder={t('survey.question3.placeholder')}
              maxLength={500}
              onChange={handleChangeQuestion3Answer}
            />
            <span className="limit-length-text">
              {answers.question3.answer.length}/500
            </span>
            <div className="mt-5 wrap-submit">
              <Button
                color="primary"
                className="btn-rounded btn-submit"
                disabled={disabledCondiiton}
                onClick={handleSubmit}
              >
                <span>{t('survey.submit')}</span>
              </Button>
            </div>
          </Form>
        </OffcanvasBody>
      </Offcanvas>
      <ToastContainer />
    </SurveyContext.Provider>
  );
};
